import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { useCarousel } from "@/hooks/useCarousel";
import { TESTIMONIALS } from "@/data/content";

const navBtn =
    "flex h-11 w-11 items-center justify-center rounded-full border border-wood/20 text-wood transition-colors duration-300 hover:border-ink hover:bg-ink hover:text-ivory";
const QUOTE_MOTION = {
    initial: { opacity: 0, y: 24 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -16 },
    transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
};

const Quote = ({ t }) => (
    <motion.blockquote key={t.id} data-testid={`testimonial-quote-${t.id}`} {...QUOTE_MOTION}>
        <p className="font-serif text-2xl font-light leading-snug tracking-tight text-ink sm:text-3xl lg:text-4xl">“{t.quote}”</p>
        <footer className="mt-10 flex items-center gap-4">
            <span className="h-px w-10 bg-terra/60" />
            <div>
                <p data-testid="testimonial-author" className="text-sm font-medium text-ink">{t.name}</p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-fog">{t.project}</p>
            </div>
        </footer>
    </motion.blockquote>
);

const Dots = ({ index, onSelect }) => (
    <div className="flex items-center gap-2">
        {TESTIMONIALS.map((t, i) => (
            <button
                key={t.id}
                data-testid={`testimonial-dot-${i + 1}`}
                aria-label={`Show testimonial ${i + 1}`}
                onClick={() => onSelect(i)}
                className={`h-1.5 rounded-full transition-all duration-500 ${i === index ? "w-8 bg-terra" : "w-1.5 bg-wood/25 hover:bg-wood/50"}`}
            />
        ))}
    </div>
);

const Controls = ({ index, count, onPrev, onNext }) => (
    <div className="mt-10 flex items-center gap-6">
        <div className="flex gap-2">
            <button data-testid="testimonial-prev-btn" aria-label="Previous testimonial" onClick={onPrev} className={navBtn}>
                <ArrowLeft size={16} strokeWidth={1.5} />
            </button>
            <button data-testid="testimonial-next-btn" aria-label="Next testimonial" onClick={onNext} className={navBtn}>
                <ArrowRight size={16} strokeWidth={1.5} />
            </button>
        </div>
        <span data-testid="testimonial-counter" className="font-mono text-xs tracking-[0.2em] text-fog">
            0{index + 1} / 0{count}
        </span>
    </div>
);

const Intro = () => (
    <>
        <div className="flex items-center gap-4">
            <span className="font-mono text-sm text-terra">03</span>
            <span className="h-px w-10 bg-terra/50" />
            <span className="text-xs uppercase tracking-[0.25em] text-timber">Client Voices</span>
        </div>
        <h2 className="mt-6 font-serif text-2xl font-normal leading-snug tracking-tight text-ink sm:text-3xl lg:text-4xl">
            Homes That Still Feel Right, Years Later
        </h2>
        <p className="mt-6 text-base font-light leading-relaxed text-mute">
            A few words from the families and businesses we've built alongside across Kolkata.
        </p>
    </>
);

export const Testimonials = () => {
    const count = TESTIMONIALS.length;
    const { index, next, prev, select } = useCarousel(count);

    return (
        <section data-testid="home-testimonials" className="relative overflow-hidden border-y border-wood/10 py-20 lg:py-32">
            <span aria-hidden className="pointer-events-none absolute -left-4 -top-10 select-none font-serif text-[16rem] italic leading-none text-wood/[0.05] lg:text-[26rem]">
                “
            </span>
            <div className="relative mx-auto grid max-w-7xl grid-cols-1 gap-12 px-6 sm:px-12 lg:grid-cols-12 lg:gap-16 lg:px-20">
                <Reveal className="lg:col-span-4">
                    <Intro />
                    <Controls index={index} count={count} onPrev={prev} onNext={next} />
                </Reveal>
                <Reveal delay={0.15} className="lg:col-span-8">
                    <div className="min-h-[260px] sm:min-h-[240px]">
                        <AnimatePresence mode="wait">
                            <Quote t={TESTIMONIALS[index]} />
                        </AnimatePresence>
                    </div>
                    <div className="mt-10">
                        <Dots index={index} onSelect={select} />
                    </div>
                </Reveal>
            </div>
        </section>
    );
};
